import {Component, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {ConfirmationService, MessageService} from 'primeng/api';
import {forkJoin, Observable} from 'rxjs';
import {
  OfferSetValidationCondition,
  OfferSetValidationDataField,
  OfferSetValidationOperator,
  OfferSetValidationRuleModel,
  OfferSetValidationRulePayload,
  OfferSetValidationValueFormat,
  offerSetValidationConditions,
  offerSetValidationDataFieldConfigurations,
  offerSetValidationDataFields,
  offerSetValidationOperators,
  offerSetValidationValueFormats
} from '../../../../model/offer-set-validation-rule.model';
import {OfferSetValidationService} from '../../../../service/offer-set-validation.service';

interface OfferSetValidationRuleFormControls {
  id: FormControl<number | null>;
  vehicleCondition: FormControl<OfferSetValidationCondition>;
  dataField: FormControl<OfferSetValidationDataField>;
  operator: FormControl<OfferSetValidationOperator>;
  value: FormControl<number | null>;
  enabled: FormControl<boolean>;
  created: FormControl<string | null>;
  modified: FormControl<string | null>;
}

interface SelectOption<T> {
  label: string;
  value: T;
}

interface OfferSetValidationRuleFormValue {
  id: number | null;
  vehicleCondition: OfferSetValidationCondition;
  dataField: OfferSetValidationDataField;
  operator: OfferSetValidationOperator;
  value: number | null;
  enabled: boolean;
  created: string | null;
  modified: string | null;
}

type OfferSetValidationRuleForm = FormGroup<OfferSetValidationRuleFormControls>;

const defaultDataField = offerSetValidationDataFields.msrp;
const defaultOperator = offerSetValidationOperators.greaterThan;
const defaultRuleValue = 0;
const messageLife = 5000;

@Component({
  selector: 'auto-offer-set-validation',
  templateUrl: './offer-set-validation.component.html',
  standalone: false
})
export class OfferSetValidationComponent implements OnInit {
  readonly rulesForm: FormGroup<{
    rules: FormArray<OfferSetValidationRuleForm>;
  }>;
  readonly conditionOptions: Array<SelectOption<OfferSetValidationCondition>> = [
    {label: 'New', value: offerSetValidationConditions.new},
    {label: 'Used', value: offerSetValidationConditions.used}
  ];
  readonly dataFieldOptions = this.buildDataFieldOptions();
  loading = true;
  saving = false;
  deleting = false;

  /** Initializes the reactive rule form and its supporting services. */
  constructor(
    private formBuilder: FormBuilder,
    private titleService: Title,
    private offerSetValidationService: OfferSetValidationService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.rulesForm = this.formBuilder.group({
      rules: this.formBuilder.array<OfferSetValidationRuleForm>([])
    });
  }

  /** Sets the browser title and loads the current rules. */
  ngOnInit(): void {
    this.titleService.setTitle('Offer Set Validation');
    this.loadRules();
  }

  /** Provides the rule form array used by the editable table. */
  get rules(): FormArray<OfferSetValidationRuleForm> {
    const rules = this.rulesForm.controls.rules;

    return rules;
  }

  /** Appends a new dirty rule with the configured safe defaults. */
  addRule(): void {
    const rule = this.createRuleForm(null);
    rule.markAsDirty();
    this.rules.push(rule);
    this.rulesForm.markAsDirty();
  }

  /** Requests confirmation before removing a rule from the form. */
  removeRule(index: number): void {
    if (!this.canRemoveRule(index)) {
      return;
    }

    this.confirmationService.confirm({
      key: 'deleteOfferSetValidationRule',
      defaultFocus: 'none',
      message: 'Are you sure you want to remove this offer set validation rule?',
      header: 'Delete Offer Set Validation Rule',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary'
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.confirmationService.close();
        this.deleteRule(index);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /** Ensures the selected operator remains valid when the data field changes. */
  handleDataFieldChange(rule: OfferSetValidationRuleForm): void {
    const operatorOptions = this.getOperatorOptions(rule);
    const selectedOperator = rule.controls.operator.value;
    const selectedOperatorIsAllowed = operatorOptions.some(option => option.value === selectedOperator);

    if (!selectedOperatorIsAllowed) {
      const firstOperatorOption = operatorOptions[0];
      rule.controls.operator.setValue(firstOperatorOption.value);
    }
  }

  /** Builds the operators allowed for the selected data field. */
  getOperatorOptions(
    rule: OfferSetValidationRuleForm
  ): Array<SelectOption<OfferSetValidationOperator>> {
    const dataField = rule.controls.dataField.value;
    const configuration = offerSetValidationDataFieldConfigurations[dataField];
    const options = configuration.operators.map(operator => ({
      label: operator,
      value: operator
    }));

    return options;
  }

  /** Returns the value format configured for the selected data field. */
  getValueFormat(rule: OfferSetValidationRuleForm): OfferSetValidationValueFormat {
    const dataField = rule.controls.dataField.value;
    const configuration = offerSetValidationDataFieldConfigurations[dataField];
    const valueFormat = configuration.valueFormat;

    return valueFormat;
  }

  /** Indicates whether the selected rule value should use currency formatting. */
  isCurrencyValue(rule: OfferSetValidationRuleForm): boolean {
    const isCurrency = this.getValueFormat(rule) === offerSetValidationValueFormats.currency;

    return isCurrency;
  }

  /** Indicates whether the selected rule value should use percentage formatting. */
  isPercentValue(rule: OfferSetValidationRuleForm): boolean {
    const isPercent = this.getValueFormat(rule) === offerSetValidationValueFormats.percent;

    return isPercent;
  }

  /** Indicates whether the current form can submit its dirty rules. */
  canSave(): boolean {
    const canSave = this.rulesForm.valid && this.rulesForm.dirty && !this.saving && !this.deleting;

    return canSave;
  }

  /** Saves all dirty new and existing rules through the frontend service. */
  saveChanges(): void {
    if (this.canSave()) {
      const requests = this.buildSaveRequests();
      this.saving = true;
      this.rulesForm.disable({emitEvent: false});
      forkJoin(requests).subscribe({
        next: () => this.handleSaveSuccess(),
        error: () => this.handleSaveError()
      });
    }
  }

  /** Reloads the persisted rules when the form has unsaved changes. */
  cancelChanges(): void {
    if (this.rulesForm.dirty && !this.saving) {
      this.loadRules();
    }
  }

  /** Exposes the form dirty state to the navigation guard. */
  hasUnsavedRuleChanges(): boolean {
    const hasUnsavedChanges = this.rulesForm.dirty;

    return hasUnsavedChanges;
  }

  /** Checks whether a row can begin the deletion flow. */
  private canRemoveRule(index: number): boolean {
    const canRemove = !this.saving && !this.deleting && index >= 0 && index < this.rules.length;

    return canRemove;
  }

  /** Deletes a persisted row or removes a new row locally. */
  private deleteRule(index: number): void {
    const rule = this.rules.at(index);
    const id = rule.controls.id.value;

    if (id === null) {
      this.removeLocalRule(index);

      return;
    }

    this.deleting = true;
    this.rulesForm.disable({emitEvent: false});
    this.offerSetValidationService.deleteRule(id).subscribe({
      next: () => this.handleDeleteSuccess(index),
      error: () => this.handleDeleteError()
    });
  }

  /** Removes a rule that has not yet been persisted. */
  private removeLocalRule(index: number): void {
    this.rules.removeAt(index);
    this.rulesForm.markAsDirty();
  }

  /** Updates the form after a persisted rule has been deleted. */
  private handleDeleteSuccess(index: number): void {
    this.rules.removeAt(index);
    this.deleting = false;
    this.rulesForm.enable({emitEvent: false});
    this.updateDirtyState();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Offer set validation rule was deleted.',
      life: messageLife
    });
  }

  /** Restores the form after a rule deletion request fails. */
  private handleDeleteError(): void {
    this.deleting = false;
    this.rulesForm.enable({emitEvent: false});
    this.showErrorMessage('Failed to delete offer set validation rule.');
  }

  /** Synchronizes the parent form dirty state with its individual rows. */
  private updateDirtyState(): void {
    const hasDirtyRules = this.rules.controls.some(rule => rule.dirty);

    if (hasDirtyRules) {
      this.rulesForm.markAsDirty();
    }

    if (!hasDirtyRules) {
      this.rulesForm.markAsPristine();
    }
  }

  /** Loads rules from the API and handles loading errors. */
  private loadRules(): void {
    this.loading = true;
    this.offerSetValidationService.getRules().subscribe({
      next: (rules) => {
        this.replaceRules(rules);
        this.loading = false;
        this.saving = false;
      },
      error: () => {
        this.loading = false;
        this.saving = false;
        this.rulesForm.enable({emitEvent: false});
        this.showErrorMessage('Failed to load offer set validation rules.');
      }
    });
  }

  /** Replaces the editable form rows with normalized API results. */
  private replaceRules(rules: OfferSetValidationRuleModel[]): void {
    const ruleForms = rules.map(rule => this.createRuleForm(rule));
    const rulesFormArray = this.formBuilder.array(ruleForms);
    this.rulesForm.setControl('rules', rulesFormArray);
    this.rulesForm.enable({emitEvent: false});
    this.rulesForm.markAsPristine();
  }

  /** Creates one typed reactive form group for a rule. */
  private createRuleForm(
    rule: OfferSetValidationRuleModel | null
  ): OfferSetValidationRuleForm {
    const formValue = this.buildRuleFormValue(rule);
    const ruleForm = this.formBuilder.group<OfferSetValidationRuleFormControls>({
      id: this.formBuilder.control(formValue.id),
      vehicleCondition: this.formBuilder.control(formValue.vehicleCondition),
      dataField: this.formBuilder.control(formValue.dataField, {nonNullable: true, validators: [Validators.required]}),
      operator: this.formBuilder.control(formValue.operator, {nonNullable: true, validators: [Validators.required]}),
      value: this.formBuilder.control(formValue.value, [Validators.required, this.finiteNumberValidator]),
      enabled: this.formBuilder.control(formValue.enabled, {nonNullable: true}),
      created: this.formBuilder.control(formValue.created),
      modified: this.formBuilder.control(formValue.modified)
    });

    return ruleForm;
  }

  /** Applies persisted values or safe defaults to a form row. */
  private buildRuleFormValue(
    rule: OfferSetValidationRuleModel | null
  ): OfferSetValidationRuleFormValue {
    let formValue: OfferSetValidationRuleFormValue = {
      id: null,
      vehicleCondition: offerSetValidationConditions.new,
      dataField: defaultDataField,
      operator: defaultOperator,
      value: defaultRuleValue,
      enabled: true,
      created: null,
      modified: null
    };

    if (rule !== null) {
      formValue = {...rule};
    }

    return formValue;
  }

  /** Validates that a form value is a finite number when supplied. */
  private finiteNumberValidator(control: AbstractControl): ValidationErrors | null {
    let validationErrors: ValidationErrors | null = null;
    const value = control.value;

    if (value !== null && value !== '' && (typeof value !== 'number' || !Number.isFinite(value))) {
      validationErrors = {finiteNumber: true};
    }

    return validationErrors;
  }

  /** Builds the selectable data-field options for the rule form. */
  private buildDataFieldOptions(): Array<SelectOption<OfferSetValidationDataField>> {
    const dataFields = Object.values(offerSetValidationDataFields);
    const options = dataFields.map(dataField => {
      const configuration = offerSetValidationDataFieldConfigurations[dataField];

      return {
        label: configuration.label,
        value: dataField
      };
    });

    return options;
  }

  /** Creates save requests for every dirty rule in the form. */
  private buildSaveRequests(): Array<Observable<OfferSetValidationRuleModel>> {
    const dirtyRules = this.rules.controls.filter(rule => rule.dirty);
    const requests = [
      ...this.buildCreateRequests(dirtyRules),
      ...this.buildUpdateRequests(dirtyRules)
    ];

    return requests;
  }

  /** Creates POST requests for dirty rules without persisted identifiers. */
  private buildCreateRequests(
    rules: OfferSetValidationRuleForm[]
  ): Array<Observable<OfferSetValidationRuleModel>> {
    const newRules = rules.filter(rule => rule.controls.id.value === null);
    const requests = newRules.map(rule => {
      const payload = this.buildPayload(rule);

      return this.offerSetValidationService.createRule(payload);
    });

    return requests;
  }

  /** Creates PATCH requests for dirty rules with persisted identifiers. */
  private buildUpdateRequests(
    rules: OfferSetValidationRuleForm[]
  ): Array<Observable<OfferSetValidationRuleModel>> {
    const existingRules = rules.filter(rule => rule.controls.id.value !== null);
    const requests: Array<Observable<OfferSetValidationRuleModel>> = [];

    existingRules.forEach(rule => {
      const id = rule.controls.id.value;

      if (id !== null) {
        const payload = this.buildPayload(rule);
        requests.push(this.offerSetValidationService.updateRule(id, payload));
      }
    });

    return requests;
  }

  /** Maps editable form values to the writable API payload. */
  private buildPayload(rule: OfferSetValidationRuleForm): OfferSetValidationRulePayload {
    const formValue = rule.getRawValue();
    let value = defaultRuleValue;

    if (formValue.value !== null) {
      value = formValue.value;
    }
    const payload: OfferSetValidationRulePayload = {
      vehicleCondition: formValue.vehicleCondition,
      dataField: formValue.dataField,
      operator: formValue.operator,
      value,
      enabled: formValue.enabled
    };

    return payload;
  }

  /** Displays success feedback and reloads normalized saved rules. */
  private handleSaveSuccess(): void {
    this.rulesForm.markAsPristine();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Offer set validation rules were saved.',
      life: messageLife
    });
    this.loadRules();
  }

  /** Restores the editable form and displays save failure feedback. */
  private handleSaveError(): void {
    this.saving = false;
    this.rulesForm.enable({emitEvent: false});
    this.showErrorMessage('Failed to save offer set validation rules.');
  }

  /** Displays a standard error notification for the current operation. */
  private showErrorMessage(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail,
      life: messageLife
    });
  }
}
